/**
 * The rotating line under "Loading your conversation…": twenty playful words,
 * five of them Swabian, shuffled once per mount and iterated in that order.
 * Every third slot is the "Powered by Blockbrain" mark instead of a word.
 * Pure on purpose — the frontend suite has no DOM, so the sequence is tested here.
 */

export const STANDARD_LOADING_WORDS = [
  'Thinking',
  'Pondering',
  'Brewing',
  'Untangling',
  'Gathering',
  'Polishing',
  'Connecting dots',
  'Warming up',
  'Tidying up',
  'Remembering',
  'Sorting',
  'Weaving',
  'Sharpening',
  'Assembling',
  'Fine-tuning',
] as const;

/** Swabian — "work, work", "quick", "grumbling", "a tiny bit", "on the quiet". */
export const SWABIAN_LOADING_WORDS = [
  'Schaffe, schaffe',
  'Gschwind',
  'Bruddla',
  'Muggeseggele',
  'Hälinga',
] as const;

export const LOADING_WORDS: readonly string[] = [...STANDARD_LOADING_WORDS, ...SWABIAN_LOADING_WORDS];

/** How long each slot stays on screen. */
export const LOADING_WORD_INTERVAL_MS = 1600;

export type LoadingSlot = { kind: 'word'; text: string } | { kind: 'brand' };

/** Fisher–Yates over a copy; `rng` is injectable so tests are deterministic. */
export function shuffleLoadingWords(rng: () => number = Math.random): string[] {
  const order = [...LOADING_WORDS];
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

/**
 * Slot for the given tick: ticks 2, 5, 8, … are the brand; the rest walk
 * through `order` without skipping a word, wrapping after the last one.
 */
export function loadingSlotAt(tick: number, order: readonly string[]): LoadingSlot {
  if (tick % 3 === 2) return { kind: 'brand' };
  const wordIndex = tick - Math.floor((tick + 1) / 3);
  return { kind: 'word', text: order[wordIndex % order.length] };
}
